/**
 * The wire contract of the fiscal job engine (#768) — what `/fiscal/jobs/*` answers and what the progress hub
 * pushes.
 *
 * ### Why these are string unions and not enums
 *
 * The server sends every state as its camelCase **name** (`FiscalJobStateMachine.WireName`), never as a number.
 * Two reasons, and both have bitten this codebase before: a numeric enum on the wire is unreadable in a network
 * tab and in a bug report, and — the sharper one — a `?fields=` style projection can drop a property's JSON
 * converter and send the ORDINAL instead, at which point a client that branched on numbers silently reads the
 * wrong state. A string union is the same thing a `const` object would be, without a runtime import.
 *
 * ### Nothing here names a provider
 *
 * That is the point of Feature #241. No field carries the far end's own identity: `errorDetail` is sanitised
 * **server-side before it is stored**, and the far end's operation handle is not on this contract at all. A UI
 * built on these types cannot leak a provider name, because it never receives one.
 */

/**
 * What a run does. Append-only — the server's enum is.
 *
 * `tseExport` (#776) is the export of the technical security device's own signed log messages, the second of
 * the two exports § 4 KassenSichV names. It is its own kind rather than a flag on `dataExport` because the
 * two are different artefacts, produced through different interfaces and read on different screens — and
 * because the kind is what each screen filters the engine's one run list by.
 */
export type FiscalJobKind = 'dataExport' | 'closingCorrection' | 'tseExport';

/**
 * Where a whole run stands. Derived server-side from its entries and recomputed on every write, so a client
 * never computes it.
 *
 * `completedWithErrors` is **not** a failure: every entry settled, at least one did not succeed **and at least
 * one did**. `failed` is the run that produced nothing — either it could not start at all, or every entry
 * settled and none succeeded.
 *
 * That last case used to arrive as `completedWithErrors` (Bug #789), which put „Teilweise fertig" above three
 * entries each reading „Fehlgeschlagen". A header may not contradict the rows beneath it. The two ways into
 * `failed` are told apart by `errorCode` on the summary: the run that could not start carries one, and the run
 * whose entries each failed does not, because those reasons belong to the entries.
 */
export type FiscalJobState = 'queued' | 'running' | 'completed' | 'completedWithErrors' | 'failed';

/**
 * Where one entry of a run stands.
 *
 * The four unsuccessful states are deliberately distinct and a UI must keep them so: `failed` is the only one
 * that says something is wrong with what was sent, `expired` means it has to be started again, `cancelled`
 * means it was stopped, and `withdrawn` means the result no longer exists at the far end. Collapsing them into
 * one "error" badge would tell every one of those operators the same, mostly wrong, thing.
 */
export type FiscalJobItemState =
  'pending' | 'working' | 'completed' | 'failed' | 'cancelled' | 'expired' | 'withdrawn';

/** The item states that will not change again without a retry. */
export const FISCAL_JOB_ITEM_TERMINAL_STATES: readonly FiscalJobItemState[] =
  ['completed', 'failed', 'cancelled', 'expired', 'withdrawn'];

/** The run states that will not change again. */
export const FISCAL_JOB_FINISHED_STATES: readonly FiscalJobState[] =
  ['completed', 'completedWithErrors', 'failed'];

/** Whether a run has finished — the only sanctioned way to ask. */
export function isFiscalJobFinished(state: FiscalJobState): boolean {
  return FISCAL_JOB_FINISHED_STATES.includes(state);
}

/** Whether an entry has settled — the only sanctioned way to ask. */
export function isFiscalJobItemTerminal(state: FiscalJobItemState): boolean {
  return FISCAL_JOB_ITEM_TERMINAL_STATES.includes(state);
}

/**
 * Why an entry did not come out, in the order the three sources are allowed to win.
 *
 * 1. **`errorDetail`**, when there is one — the wording of whoever was closer to the fact than the code is.
 * 2. **Our own German sentence for the code**, otherwise.
 * 3. **A generic sentence**, so nobody is ever shown a raw code.
 *
 * ### Why the detail is first, although an English sentence once reached a German screen (Bug #789)
 *
 * Every entry the engine settles carries one of a handful of **engine** codes, and the poll path stamps
 * `E_FISCAL_JOB_OPERATION_FAILED` on every refusal a handler translates. So one code covers four quite
 * different German sentences a handler wrote — „Für diese Kasse liegen im gewählten Zeitraum keine
 * Kassenabschlüsse vor", „Diese Sicherheitseinrichtung wurde gelöscht", and two more — against one generic
 * sentence this screen can offer for that code. Letting the code win would have thrown all four away.
 *
 * What went wrong was never the order: it was that the **server** also filled this field with its own
 * `ErrorCatalog` description, which is English by design (API contract text, branched on by code, not UI
 * copy). So for a code the engine settles on its own — an interrupted start, an exhausted polling budget —
 * the field held nothing the code did not already say, in the wrong language. The engine no longer writes it,
 * and the read model drops it from rows written before that (`FiscalJobProjection.ToItem`), so what arrives
 * here is a detail only when it genuinely adds something.
 *
 * @param item the entry.
 * @param sentenceFor the screen's own code → German lookup, returning null when it has no sentence.
 * @param fallback what to say when neither source has anything.
 */
export function fiscalJobItemReason(
  item: Pick<FiscalJobItem, 'errorCode' | 'errorDetail'>,
  sentenceFor: (code: string | null | undefined) => string | null,
  fallback: string): string {
  return item.errorDetail ?? sentenceFor(item.errorCode) ?? fallback;
}

/** A run as the list screen reads it. The counts ride along so a row renders without a second request. */
export interface FiscalJobSummary {
  id: string;
  kind: FiscalJobKind;
  state: FiscalJobState;
  /** The location the run's work belongs to. */
  shardId: string;
  /** The principal id that started it. */
  requestedBy: string;
  createdUtc: string;
  startedUtc: string | null;
  finishedUtc: string | null;
  itemCount: number;
  completedCount: number;
  failedCount: number;
  /** Why the run could not start at all (an `E_FISCAL_JOB_*` code), or null. */
  errorCode: string | null;
  /**
   * The parameters the run was created with, as the JSON string the server stored.
   *
   * **On the summary, and nowhere else** (#770, found on the live screen). A data export's parameters are
   * what say which period a run covers, and the run list is a list of periods — without them every row read
   * „Zeitraum unbekannt" and an operator could not tell one export from another. The raw blob rather than a
   * typed period, so the row and the opened run share ONE parsing rule (`parseExportPeriod`) instead of two
   * answers to the same question.
   */
  parameters: string | null;
}

/** One entry of a run. */
export interface FiscalJobItem {
  /** What a per-entry retry names. */
  id: string;
  /** The local record this entry is about. */
  reference: string;
  label: string | null;
  state: FiscalJobItemState;
  startedUtc: string | null;
  finishedUtc: string | null;
  /** An `E_FISCAL_JOB_*` code, or null. */
  errorCode: string | null;
  /** A short description, already sanitised server-side, or null. */
  errorDetail: string | null;
  /** Where a finished entry's result can be collected — what makes a re-download work. */
  resultReference: string | null;
  /** How often an operator has re-armed this entry. */
  manualRetries: number;
  /** Whether a retry would be accepted right now. False while the entry is still running. */
  retryable: boolean;
}

/**
 * A run with its entries — what the detail view reads and what every refetch returns.
 *
 * **No `parameters` of its own**: they live on {@link FiscalJobSummary}, reachable here as `job.parameters`.
 * The two used to be separate, with only this one carrying them, which is the whole of the period defect
 * found on the live screen (#770): the list endpoint answers summaries, so a row had nothing to read a
 * period out of.
 */
export interface FiscalJobDetail {
  job: FiscalJobSummary;
  items: FiscalJobItem[];
}

/**
 * Puts runs in the order every screen shows them: newest first, by when the run was **created**.
 *
 * ### Why this is a function and not "whatever order the server sent"
 *
 * The server answers `/fiscal/jobs/List` newest first, so a fresh list is already right — and then one run is
 * merged into it from a different response (a create, a retry, or simply opening an old run to look at it)
 * and the order becomes a property of what the user last clicked. That is Bug #789: expanding an export moved
 * it to the top of „Bisherige Exporte". Sorting on every write makes the order a property of the data, so it
 * is the same whichever response arrives and in whatever order.
 *
 * `createdUtc` and not `startedUtc` or `finishedUtc`: those are null for part of a run's life, and a list
 * whose rows move as work progresses is a list you cannot keep your place in. The id breaks ties so two runs
 * created in the same millisecond — one press of „Export erstellen" per register — keep a fixed order instead
 * of swapping on every refresh.
 *
 * @param jobs the rows to order. Not mutated; a new array comes back.
 */
export function sortFiscalJobs(jobs: readonly FiscalJobSummary[]): FiscalJobSummary[] {
  return [...jobs].sort((a, b) => {
    const at = Date.parse(a.createdUtc);
    const bt = Date.parse(b.createdUtc);
    // An unparseable instant sorts last rather than throwing the whole list into an arbitrary order: a row
    // this build cannot read the timestamp of must not take the list down with it.
    if (Number.isNaN(at) || Number.isNaN(bt)) {
      if (Number.isNaN(at) && Number.isNaN(bt)) return a.id.localeCompare(b.id);
      return Number.isNaN(at) ? 1 : -1;
    }
    return bt - at || a.id.localeCompare(b.id);
  });
}

/** One entry of a create request. */
export interface FiscalJobItemRequest {
  reference: string;
  label?: string;
}

/** What starting a run asks for. */
export interface FiscalJobCreateRequest {
  kind: FiscalJobKind;
  items: FiscalJobItemRequest[];
  /** The kind's own parameters as JSON. Opaque to the engine. */
  parameters?: string;
}

/**
 * A push from the progress hub: something on this run advanced.
 *
 * **It is a hint, never the state.** The only correct reaction is to refetch the run. The `state` here exists
 * so a client can decide whether it still cares (is this run one I am showing?), not so it can render it — a
 * dropped or reordered message would otherwise put a value on screen that the durable record disagrees with.
 */
export interface FiscalJobProgressPush {
  jobId: string;
  state: FiscalJobState;
  /** When the advance happened (ISO-8601), or null when the payload carried no instant. */
  occurredUtc: string | null;
}
